import type { Station, UpdateDeviceCalibrationInput } from '@/__generated__/graphql';
import { setSnackbar } from '@/slices/components';
import { skipToken, useMutation, useSuspenseQuery } from '@apollo/client';
import ApolloErrorBoundary from '@components/ApolloErrorBoundary';
import Loading from '@components/Loading';
import NumericTextField from '@components/NumericTextField';
import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  InputAdornment,
  Typography,
} from '@mui/material';
import { Suspense, type FC } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import UPDATE_DEVICE_CALIBRATION from '../graphql/mutations/updateDeviceCalibration';
import GET_DEVICE_CALIBRATION from '../graphql/queries/getDeviceCalibration';
import { schema } from '../utils/validationSchema';
import { useLingui } from '@lingui/react';
import { Trans, msg } from '@lingui/macro';

interface Props {
  device: Station;
  handleClose: (refetch?: boolean) => void;
  setDirtyForm: (value: boolean) => void;
}

const CalibrationLevelTab: FC<Props> = (props) => {
  if (!props.device.id) return <p>Seleccione un dispositivo</p>;

  return (
    <ApolloErrorBoundary>
      <Suspense fallback={<Loading />}>
        <FormComponent {...props} />
      </Suspense>
    </ApolloErrorBoundary>
  );
};

type FormValues = UpdateDeviceCalibrationInput;

const FormComponent: FC<Props> = ({ device, handleClose }) => {
  const { _ } = useLingui();
  const { data } = useSuspenseQuery(
    GET_DEVICE_CALIBRATION,
    // https://www.apollographql.com/docs/react/data/suspense/#skipping-suspense-hooks
    device.id ? { variables: { deviceId: device.id } } : skipToken,
  );

  const dispatch = useDispatch();

  const defaultValues: FormValues = {
    deviceId: device.id,
    calibration: {
      level: data?.deviceDataConfiguration.calibration?.level,
    },
  };

  const methods = useForm<FormValues>({
    defaultValues,
    resolver: yupResolver<FormValues>(schema),
  });

  const [update, { loading: updateLoading }] = useMutation(UPDATE_DEVICE_CALIBRATION, {
    onCompleted: (data) => {
      methods.reset({
        deviceId: device.id,
        calibration: {
          level: data.updateDeviceCalibration.calibration?.level,
        },
      });
      dispatch(
        setSnackbar({
          open: true,
          message: _(msg`Dato actualizado correctamente`),
          severity: 'success',
        }),
      );
    },
    onError: (errors) => {
      dispatch(
        setSnackbar({
          open: true,
          message: `${_(msg`Error al actualizar el dato`)}: ${errors.message}`,
          severity: 'error',
        }),
      );
    },
  });

  const { isDirty, isSubmitting } = methods.formState;

  const submitHandler = async (formData: FormValues) => {
    update({ variables: { input: formData } });
  };

  return (
    <form onSubmit={methods.handleSubmit(submitHandler)}>
      <DialogContent
        sx={{
          maxWidth: 'md',
          minHeight: 500,
          margin: 'auto',
          borderBottom: '1px solid',
          borderColor: 'divider',
          pb: 0,
        }}
      >
        <Typography variant="h5" mb={2}>
          <Trans>Calibración Altura</Trans>
        </Typography>
        <Box display="grid" gridTemplateColumns="repeat(3, 1fr)" fontSize="small" gap={2} mb={3}>
          <Controller
            name="calibration.level"
            control={methods.control}
            render={({ field, fieldState: { error } }) => (
              <NumericTextField
                {...field}
                size="small"
                label={_(msg`Nivel de calibración`)}
                InputProps={{
                  endAdornment: <InputAdornment position="end">cm</InputAdornment>,
                }}
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ maxWidth: 'md', margin: 'auto', py: 2 }}>
        <Button
          disabled={!isDirty}
          color="info"
          variant="outlined"
          onClick={() => methods.reset(defaultValues)}
        >
          <Trans>Restablecer Formulario</Trans>
        </Button>
        <Box flexGrow={1} />
        <Button color="info" variant="outlined" onClick={() => handleClose()}>
          <Trans>Cerrar</Trans>
        </Button>
        <LoadingButton
          disabled={!isDirty}
          loading={isSubmitting || updateLoading}
          type="submit"
          variant="contained"
        >
          <Trans>Guardar</Trans>
        </LoadingButton>
      </DialogActions>
    </form>
  );
};

export default CalibrationLevelTab;
